import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, ChevronDown, ArrowRight } from 'lucide-react';
import { LIFECYCLE_COLOR_MAP, LIFECYCLE_TRANSITIONS } from '@/lib/assetTypes';
import { adminApi } from '@/lib/adminApi';

/**
 * LifecyclePill — color-coded status pill with click-to-transition popover.
 *
 * Props:
 *   - assessment: {id, status, publish_target, publish_target_suggestion}
 *   - userRole: 'hm_admin' | 'hm_advisor' | 'hm_reviewer'
 *   - onUpdated(newAssessment): callback when a transition succeeds
 */
export function LifecyclePill({ assessment, userRole, userId, onUpdated }) {
  const { t, i18n } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState(assessment?.publish_target || assessment?.publish_target_suggestion || '');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    adminApi.studioMeta().then(setMeta).catch(() => { /* non-fatal */ });
  }, []);

  if (!assessment?.status) return null;

  const stage = meta?.lifecycle_stages?.find((s) => s.value === assessment.status);
  const stageLabel = stage ? (i18n.language?.startsWith('es') ? stage.label_es : stage.label_en) : assessment.status;
  const color = LIFECYCLE_COLOR_MAP[assessment.status] || LIFECYCLE_COLOR_MAP.draft;
  const transitions = LIFECYCLE_TRANSITIONS[assessment.status] || [];
  const allowed = transitions.filter((tr) => {
    if (tr.adminOnly && userRole !== 'hm_admin') return false;
    // UAT #2B — "Request changes" is a reviewer→author action; hide it when
    // the reviewer IS the author (self-authored assessments).
    if (tr.label === 'request_changes' && userId && assessment.created_by === userId) return false;
    return true;
  });
  const canTransition = allowed.length > 0;

  // UAT #1C/#1D/#2A — surface the natural next lifecycle step as an explicit,
  // discoverable action button. Admins skip review on their own drafts
  // (draft → approve directly); advisors submit for review.
  const NEXT_STEP = {
    draft: ['approved', 'submitted_for_review'],
    needs_revision: ['approved', 'submitted_for_review'],
    submitted_for_review: ['approved'],
    approved: ['published'],
  };
  const NEXT_LABELS_EN = {
    submitted_for_review: 'Submit for review',
    approved: 'Approve',
    published: 'Publish',
  };
  const primary = (NEXT_STEP[assessment.status] || [])
    .map((to) => allowed.find((tr) => tr.to === to))
    .find(Boolean) || null;

  const handleApply = async () => {
    if (!target) return;
    setTransitioning(true);
    try {
      const extras = { notes };
      const targetMeta = transitions.find((tr) => tr.to === target);
      if (targetMeta?.requiresPublishTarget) {
        if (!publishTarget) {
          toast.error(t('studio.lifecycle.publishTargetRequired', { defaultValue: 'Pick a publish target first.' }));
          setTransitioning(false);
          return;
        }
        extras.publish_target = publishTarget;
      }
      const { assessment: updated } = await adminApi.studioTransitionAssessment(
        assessment.id, target, extras,
      );
      toast.success(t('studio.lifecycle.success', { defaultValue: 'Lifecycle updated.' }));
      setTarget(null);
      setNotes('');
      if (onUpdated) onUpdated(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed');
    } finally {
      setTransitioning(false);
    }
  };

  const pill = (
    <Badge
      variant="outline"
      data-testid={`studio-lifecycle-pill-${assessment.status}`}
      className={`${color.bg} text-xs font-semibold uppercase tracking-wide px-2.5 py-1 gap-1`}
    >
      {stageLabel}
      {canTransition && <ChevronDown className="h-3 w-3" />}
    </Badge>
  );

  if (!canTransition) return pill;

  return (
    <div className="inline-flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid="studio-lifecycle-trigger">
            {pill}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-4">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground" data-testid="studio-lifecycle-flow-hint">
              {t('studio.lifecycle.flowHint', { defaultValue: 'Flow: Draft → In review → Approved → Published' })}
            </p>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                {t('studio.lifecycle.transitionTo', { defaultValue: 'Transition to' })}
              </p>
              <Select value={target || ''} onValueChange={setTarget}>
                <SelectTrigger data-testid="studio-lifecycle-target">
                  <SelectValue placeholder={t('studio.lifecycle.pickTarget', { defaultValue: 'Select…' })} />
                </SelectTrigger>
                <SelectContent>
                  {allowed.map((tr) => {
                    const m = meta?.lifecycle_stages?.find((s) => s.value === tr.to);
                    const stageLbl = m ? (i18n.language?.startsWith('es') ? m.label_es : m.label_en) : tr.to;
                    const label = tr.label
                      ? t(`studio.lifecycle.labels.${tr.label}`, { defaultValue: stageLbl })
                      : stageLbl;
                    return (
                      <SelectItem
                        key={tr.to}
                        value={tr.to}
                        data-testid={`studio-lifecycle-option-${tr.to}`}
                      >
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {transitions.find((tr) => tr.to === target)?.requiresPublishTarget && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  {t('studio.lifecycle.publishTarget', { defaultValue: 'Publish target' })}
                </p>
                <Select value={publishTarget} onValueChange={setPublishTarget}>
                  <SelectTrigger data-testid="studio-lifecycle-publish-target">
                    <SelectValue placeholder={t('studio.lifecycle.pickPublishTarget', { defaultValue: 'Select destination…' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.publish_targets || []).map((p) => (
                      <SelectItem
                        key={p.value}
                        value={p.value}
                        data-testid={`studio-publish-target-${p.value}`}
                      >
                        {i18n.language?.startsWith('es') ? p.label_es : p.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                {t('studio.lifecycle.notes', { defaultValue: 'Notes (optional)' })}
              </p>
              <Textarea
                data-testid="studio-lifecycle-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="text-sm"
              />
            </div>

            <Button
              className="w-full"
              disabled={!target || transitioning}
              onClick={handleApply}
              data-testid="studio-lifecycle-apply"
            >
              {transitioning && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('studio.lifecycle.apply', { defaultValue: 'Apply transition' })}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {primary && (
        <Button
          size="sm"
          className="h-7 px-2.5 text-xs font-semibold gap-1"
          data-testid="studio-lifecycle-next-action"
          onClick={() => { setTarget(primary.to); setOpen(true); }}
        >
          {t(`studio.lifecycle.next.${primary.to}`, { defaultValue: NEXT_LABELS_EN[primary.to] || primary.to })}
          <ArrowRight className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export default LifecyclePill;
